import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { readField } from '../common/utils/objectUtilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const crewSlice = createSlice({
    name: 'crew',
    initialState: {
        data: [],
        kinds: []
    },
    reducers: {
        setKinds: (state, action) => {
            state.kinds = action.payload;
        },
        set: (state, action) => {
            state.data = ensureArray(action.payload);
        },
        addOrUpdateKind: (state, action) => {
            for (const kind of ensureArray(action.payload)) {
                const idx = state.kinds.findIndex(k => k.kind === kind.kind);
                if (idx > -1) {
                    state.kinds.splice(idx, 1, kind);
                } else {
                    state.kinds.push(kind);
                }
            }
        },
        removeKind: (state, action) => {
            for (const kind of ensureArray(action.payload)) {
                const idx = state.kinds.findIndex(k => k.kind === kind.kind);
                if (idx > -1) {
                    state.kinds.splice(idx, 1);
                }
            }
        },
        addOrUpdate: (state, action) => {
            for (const crew of ensureArray(action.payload)) {
                let idx = state.data.findIndex(c => c.callsign === crew.callsign);
                if (idx >= 0) {
                    state.data.splice(idx, 1, crew);
                } else {
                    state.data.push(crew);
                }
            }
        },
        remove: (state, action) => {
            for (const crew of ensureArray(action.payload)) {
                let idx = state.data.findIndex(c => c.callsign === crew.callsign);
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => crewSlice.getInitialState());
    }
});

// #region network operations
const fetchCrewKinds = network => network.get('/api/v1/crewKinds', crewSlice.actions.setKinds);
const fetchCrews = network => network.get('/api/v1/crews', crewSlice.actions.set);
const fetchCrew = callsign => network => network.get('/api/v1/crew/' + encodeURIComponent(callsign), crewSlice.actions.addOrUpdate);

export const updateCrew = (network, crew) => network.put('/api/v1/crew', crew, crewSlice.actions.addOrUpdate);
export const removeCrew = (network, crew) => network.delete('/api/v1/crew/' + encodeURIComponent(crew.callsign), crewSlice.actions.remove);
export const insertCrew = (network, crew) => network.post('/api/v1/crew', crew, crewSlice.actions.addOrUpdate);

export const createCrewKind = (network, kind) => network.post('/api/v1/crewKind', kind, crewSlice.actions.addOrUpdateKind);
export const updateCrewKind = (network, kind) => network.put('/api/v1/crewKind', kind, crewSlice.actions.addOrUpdateKind);
export const removeCrewKind = (network, kind) => network.delete('/api/v1/crewKind/' + encodeURIComponent(kind.kind), crewSlice.actions.removeKind);
// #endregion

// #region selectors
const selectCrewKinds = state => state.crew.kinds;
const selectCrews = state => state.crew.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectCrewKindByName = createSelector([selectCrewKinds, (kinds, name) => name], (kinds, name) => kinds.find(k => k.kind === name));
const selectCrewKind = name => state => selectCrewKindByName(state, name);

const selectCrewByCallsign = createSelector([selectCrews, (crews, callsign) => callsign], (crews, callsign) => crews.find(c => c.callsign === callsign) ?? {});
const selectCrew = callsign => state => selectCrewByCallsign(state, callsign);

const selectCrewByJobID = createSelector([selectCrews, (crews, jobid) => jobid], (crews, jobid) => crews.filter(c => c.jobid?.toString() === jobid?.toString()));
const selectJobCrews = jobid => state => selectCrewByJobID(state, jobid);
// #endregion

// #region listeners
const crewKindListen = {
    crewKindCreated: (kind, { dispatch }) => dispatch(crewSlice.actions.addOrUpdateKind(kind)),
    crewKindUpdated: (kind, { dispatch }) => dispatch(crewSlice.actions.addOrUpdateKind(kind)),
    crewKindRemoved: (kind, { dispatch }) => dispatch(crewSlice.actions.removeKind(kind))
}

const crewListen = {
    crewCreated: (crew, { dispatch }) => dispatch(crewSlice.actions.addOrUpdate(crew)),
    crewUpdated: (crew, { dispatch }) => dispatch(crewSlice.actions.addOrUpdate(crew)),
    crewDeleted: (crew, { dispatch }) => dispatch(crewSlice.actions.remove(crew))
}
// #endregion

// #region utilities
export function getDescriptor(descriptorDef, crew) {
    return descriptorDef?.map((d, i) => {
        let toReturn = '';
        let data = null;
        if (d.field) {
            data = readField(crew, d.field);
        }
        if (data || !d.field) {
            if (i > 0 && d.separator) {
                toReturn += d.separator;
            }
            if (d.prefix) {
                toReturn += d.prefix;
            }
            if (data) {
                toReturn += data;
            }
            if (d.suffix) {
                toReturn += d.suffix;
            }
        }
        return toReturn;
    })?.join('') ?? '';
}
// #endregion

// #region permission checks
export const canInsertCrewKind = (credentials, kind) => credentials.access.hasFull('administrator');
export const canUpdateCrewKind = canInsertCrewKind;
export const canRemoveCrewKind = canInsertCrewKind; 

export const canInsertCrew = (credentials, crew) => credentials.access.hasFull({ type: crew.type, site: crew.site, level: crew.level });
export const canUpdateCrew = canInsertCrew;
export const canRemoveCrew = canInsertCrew;
// #endregion

/**
 * Get a single crew by callsign
 * @param callsign the callsign of the crew
 */
export const [useCrew,realizeCrew] = dataHookFactory(selectCrew, initMapFactory(fetchCrew, crewListen));
export const [useCrews] = dataHookFactory(selectCrews, { run: fetchCrews, listen: crewListen });
/**
 * @description Get all crews assigned to a given job
 * @param {string | number} jobid the jobid to which the crews are attached
 */
export const [useJobCrews] = dataHookFactory(selectJobCrews, initMapFactory(jobid => fetchCrews, crewListen));
export const [useCrewKind] = dataHookFactory(selectCrewKind, name => ({ run : fetchCrewKinds, listen: crewKindListen }));
export const [useCrewKinds] = dataHookFactory(selectCrewKinds, { run: fetchCrewKinds, listen: crewKindListen });

const reducerDetails = { name: crewSlice.name, reducer: crewSlice.reducer };
export default reducerDetails;